import { PlaygroundFiles, PlaygroundFile } from '@playground/models/files.model';
import { TestsResults } from '@playground/SQL/models/tests.model';
import { loadPyodide, PyodideInterface, PyProxy } from 'pyodide';
// eslint-disable-next-line
// @ts-ignore
import PythonBoilerplateCode from '../python/python.py';
// eslint-disable-next-line
// @ts-ignore
import SQLBoilerplateCode from '../python/sql.py';
import { parsePyTestJsonReportToObj } from '@playground/SQL/utils/py-test';
import { getLastError } from '@playground/SQL/utils/traceback';

interface Props {
  stdout?: (msg: string) => void;
  stderr?: (error: string) => void;
  packages?: string[];
  files?: PlaygroundFiles;
}

export class PyodideRuntime {
  private interpreter: PyodideInterface;
  private defaultPackages = ['micropip', 'pytest', 'sqlite3'];
  private files: PlaygroundFiles = {};
  private stdout: (msg: string) => void;
  private stderr: (error: string) => void;
  private globals: PyProxy;

  constructor(props: Props) {
    this.files = props.files;
    this.stdout = props.stdout;
    this.stderr = props.stderr;
  }

  setFiles(files: PlaygroundFiles) {
    this.files = files;
  }

  async initPyodide(sqlStr: string) {
    await this.loadInterpreter();
    await this.loadPackages();
    await this.loadMicropip();
    await this.loadFiles();
    this.loadBoilerplateCode();
    this.loadSQLSetup(sqlStr);
  }

  loadSQLSetup(sqlStr: string) {
    const runSQL = this.globals.get('run_setup');
    const output = runSQL(sqlStr);
    return output;
  }

  async loadBoilerplateCode() {
    this.interpreter.runPython(PythonBoilerplateCode as string);
    this.interpreter.runPython(SQLBoilerplateCode as string);
  }

  async loadInterpreter(): Promise<void> {
    this.interpreter = await loadPyodide({
      indexURL: `${window.location.origin}/pyodide`,
      stdout: this.stdout,
      stderr: this.stderr,
      fullStdLib: false,
    });
    this.globals = this.interpreter.globals;
  }

  async loadPackages(): Promise<void> {
    const packages = [...this.defaultPackages];
    if (packages.length > 0) {
      await this.interpreter.loadPackage(packages);
    }
  }

  async loadMicropip() {
    await this.interpreter.loadPackage('micropip');
    const micropip = this.interpreter.pyimport('micropip');
    await micropip.install('pytest-json-report');
    await micropip.install('sqlparse');
    await this.interpreter.runPythonAsync(`
      import importlib
      importlib.invalidate_caches()
    `);
  }

  async loadFiles() {
    const promises = Object.keys(this.files).map((key) => {
      return this.writeFileFromString(key, this.files[key]);
    });
    await Promise.all(promises);
  }

  async updateFiles(files?: PlaygroundFiles, reloadModules = false) {
    this.setFiles(files);
    await this.loadFiles();
    if (reloadModules) {
      await this.reloadModules();
    }
  }

  async reloadModules() {
    const reloadModuleMock = this.globals.get(`reload_module`);
    this.getVisibleFiles()
      .filter((file) => file.path.match(/^.*\.(py)$/) !== null)
      // .filter((file) => !file.path.includes('_test.py'))
      .forEach((file) => {
        const [name] = file.path.split('.');
        try {
          reloadModuleMock(name).toJs();
        } catch (error) {
          // TODO: Detect syntax errors
          console.log(error);
        }
      });
  }

  async writeFileFromString(path: string, file: PlaygroundFile): Promise<void> {
    this.interpreter.FS.writeFile(path, file.code);
  }

  async runCode(code: string) {
    try {
      const runSQL = this.globals.get('run_sql');
      const output = runSQL(code).toJs();
      return output;
    } catch (error) {
      if (error.name === 'PythonError') {
        const reformatException = this.globals.get('reformat_exception');
        const messageError = getLastError(reformatException);
        if (messageError) {
          this.stderr(messageError);
        }
      }
    }
  }

  async runTests(): Promise<TestsResults> {
    try {
      const runTests = this.globals.get(`run_tests`);
      const rta = runTests().toJs();
      return parsePyTestJsonReportToObj(rta);
    } catch (error) {
      if (error.name === 'PythonError') {
        console.log('PythonError', error);
      }
    }
  }

  getVisibleFiles() {
    return Object.values(this.files);
    // return Object.values(this.files).filter((item) => !item.hidden);
  }
}
